import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useGoogleLogin } from "@react-oauth/google";
import { Loader2 } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Seo from "../components/Seo";
import api from "../lib/api";

// Login SOLO con Google (ago-2026): antes también había email+contraseña, pero de los 7
// usuarios reales en producción ni uno dependía de ella (todos entraban ya con Google) —
// solo dos cuentas admin/legado tenían contraseña, y esas están borradas. El backend sigue
// aceptando /auth/login por dentro (cuenta admin sembrada en el arranque + scripts de test),
// pero esta pantalla ya no ofrece ningún camino hasta ahí.
export default function Login() {
  const { t } = useTranslation();
  const { loginWithGoogle, apiErr } = useAuth();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [seatsFullPlan, setSeatsFullPlan] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlisted, setWaitlisted] = useState(false);

  // Login nativo con Google (Google Identity Services, flujo auth-code).
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      setErr(""); setSeatsFullPlan(null); setBusy(true);
      try { await loginWithGoogle(code); }
      catch (e2) {
        const d = e2?.response?.data?.detail;
        if (d?.error === "seats_full") setSeatsFullPlan(d.plan);
        setErr(apiErr(e2));
      }
      finally { setBusy(false); }
    },
    onError: () => setErr(t("auth.googleError")),
  });

  // Sin formulario, el correo para la lista de espera no viene ya escrito en ningún sitio (antes
  // se reutilizaba el que el usuario había tecleado en el login por email) — se pide aquí, solo
  // cuando de verdad hace falta (plazas agotadas).
  const joinWaitlist = async () => {
    if (!waitlistEmail || !seatsFullPlan) return;
    setBusy(true);
    try {
      await api.post("/plan-seats/waitlist", { email: waitlistEmail, plan: seatsFullPlan });
      setWaitlisted(true);
    } catch (e2) { setErr(apiErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-white">
      <Seo title="Iniciar sesión — YTMethod" description={t("auth.loginSubtitle")} path="/login" noindex />
      <div className="relative w-full max-w-md bg-white border-2 border-slate-200 rounded-2xl shadow-xl p-8 sm:p-10">
        <div className="flex items-center justify-between gap-3 mb-2">
          <Link to="/" data-testid="brand-home-link">
            <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-12 w-auto hover:opacity-80 transition-opacity" data-testid="brand-logo" />
          </Link>
          <LanguageSwitcher />
        </div>
        <p className="text-lg text-slate-600 mb-8">{t("auth.loginSubtitle")}</p>

        <button
          data-testid="google-login-button"
          type="button"
          onClick={() => googleLogin()}
          disabled={busy}
          className="w-full min-h-[56px] text-lg font-bold rounded-xl bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
        >
          {busy
            ? <Loader2 className="animate-spin" size={22} />
            : (
              <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001 6.19 5.238 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
              </svg>
            )}
          {t("auth.googleContinue")}
        </button>

        {err && !seatsFullPlan && (
          <div data-testid="login-error" className="mt-5 bg-red-50 text-red-700 border-2 border-red-200 rounded-lg px-4 py-3 text-base font-medium">
            ❌ {err}
          </div>
        )}

        {seatsFullPlan && (
          waitlisted ? (
            <div data-testid="waitlist-success" className="mt-5 bg-green-50 text-green-800 border-2 border-green-200 rounded-lg px-4 py-3 text-base font-medium">
              ✅ {t("auth.waitlistJoined")}
            </div>
          ) : (
            <div data-testid="seats-full-banner" className="mt-5 bg-amber-50 text-amber-800 border-2 border-amber-200 rounded-lg px-4 py-3 text-base font-medium space-y-3">
              <p>⏳ {err}</p>
              <input
                type="email" value={waitlistEmail} onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")} data-testid="waitlist-email-input"
                className="w-full bg-white border-2 border-amber-300 rounded-lg px-3 py-2.5 text-base text-slate-900 focus:border-amber-600 outline-none"
              />
              <button data-testid="join-waitlist-button" type="button" onClick={joinWaitlist} disabled={busy || !waitlistEmail}
                className="w-full min-h-[48px] text-base font-bold rounded-xl bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="animate-spin" size={18} />} {t("auth.joinWaitlist")}
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
